<template>
  <div @click="generatePDF">
    <div class="d-flex">
      <img
        src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS1maWxlLXRleHQiPjxwYXRoIGQ9Ik0xNC41IDJINmEyIDIgMCAwIDAtMiAydjE2YTIgMiAwIDAgMCAyIDJoMTJhMiAyIDAgMCAwIDItMlY3LjVMMTQuNSAyeiIvPjxwb2x5bGluZSBwb2ludHM9IjE0IDIgMTQgOCAyMCA4Ii8+PGxpbmUgeDE9IjE2IiB4Mj0iOCIgeTE9IjEzIiB5Mj0iMTMiLz48bGluZSB4MT0iMTYiIHgyPSI4IiB5MT0iMTciIHkyPSIxNyIvPjxsaW5lIHgxPSIxMCIgeDI9IjgiIHkxPSI5IiB5Mj0iOSIvPjwvc3ZnPg=="
      >
      <div class="name">
        PDF
      </div>
    </div>
  </div>
</template>
<script>
import {
  VBTooltip,
  VBModal,
} from 'bootstrap-vue'
import autoTable from 'jspdf-autotable'
import Ripple from 'vue-ripple-directive'
import JsPDF from 'jspdf'
import ErrorMessage from '@core/mixins/ErrorMessage'

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  mixins: [ErrorMessage],
  props: {
    tableFields: {
      type: Array,
      default: () => {},
    },
    tableData: {
      type: Array,
      default: () => {},
    },
    title: {
      type: String,
      default: '',
    },
    fetch: {
      type: Function,
      default: null,
    },
    fileName: {
      type: String,
      default: 'Data Export.pdf',
    },
  },
  methods: {
    async generatePDF() {
      try {
        const doc = new JsPDF({
          orientation: 'landscape',
          format: 'a3',
        })
        // Add title
        doc.text(this.title, 14, 10)
        let dataToExport = this.tableData
        // Always fetch data from server to get complete dataset
        // This ensures we get all filtered rows or all rows based on current filters
        if (this.fetch && typeof this.fetch === 'function') {
          try {
            // Fetch data with current filters applied (gets all filtered rows, not just current page)
            const filteredData = await this.fetch()
            // Transform the fetched data to match tableData format
            dataToExport = this.transformFetchedData(filteredData)
          } catch (error) {
            // Fallback to current tableData if fetch fails
            dataToExport = this.tableData
            this.convertAndNotifyError(error)
          }
        }
        // Calculate totals for amount columns
        const totals = this.calculateTotals(dataToExport)
        // Generate table
        autoTable(doc, {
          head: [this.tableFields],
          body: dataToExport,
          columnStyles: this.getColumnStyles(),
          startY: 20,
          margin: { left: 10, right: 10 },
          tableWidth: 'auto',
          styles: {
            fontSize: 8,
            cellPadding: 2,
            overflow: 'linebreak',
            halign: 'left',
            valign: 'middle',
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 8,
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
        })
        // Add totals row if we have totals
        if (totals.length > 0) {
          autoTable(doc, {
            body: [totals],
            columnStyles: this.getColumnStyles(),
            margin: { left: 10, right: 10 },
            tableWidth: 'auto',
            styles: {
              fontSize: 8,
              fontStyle: 'bold',
              fillColor: [240, 240, 240],
              cellPadding: 2,
              overflow: 'linebreak',
              halign: 'left',
              valign: 'middle',
            },
            startY: doc.lastAutoTable.finalY + 5,
          })
        }
        // Save the PDF
        doc.save(this.fileName)
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
    transformFetchedData(data) {
      // This method should be overridden by parent components
      // to transform the fetched data into the correct format
      return data
    },
    getColumnStyles() {
      // Responsive column styles - auto-calculate based on content
      const styles = {}
      const fieldCount = this.tableFields.length
      const totalAvailableWidth = 400 // A3 landscape width minus margins
      // Calculate responsive width per column
      const responsiveWidth = Math.floor(totalAvailableWidth / fieldCount)
      // Apply responsive widths
      for (let i = 0; i < fieldCount; i += 1) {
        styles[i] = {
          cellWidth: responsiveWidth,
          fontSize: 8,
          overflow: 'linebreak',
          halign: 'left',
        }
      }
      return styles
    },
    calculateTotals(data) {
      if (!data || data.length === 0) return []
      // Define which columns contain amounts (these will be summed)
      const amountColumns = this.getAmountColumns()
      // Initialize totals array
      const totals = new Array(this.tableFields.length).fill('')
      totals[0] = 'TOTAL'
      // Calculate sums for amount columns
      data.forEach(row => {
        amountColumns.forEach(colIndex => {
          if (row[colIndex] && row[colIndex] !== '-' && row[colIndex] !== 'N/A') {
            // Remove commas and parse the number
            const value = parseFloat(row[colIndex].replace(/,/g, '')) || 0
            totals[colIndex] = (parseFloat(totals[colIndex]) || 0) + value
          }
        })
      })
      // Format the totals with commas
      amountColumns.forEach(colIndex => {
        if (totals[colIndex] !== '') {
          totals[colIndex] = totals[colIndex].toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        }
      })
      return totals
    },
    getAmountColumns() {
      // Define which columns contain amounts based on field names
      const amountFieldNames = [
        'Depositable Amount',
        'Expenses Total',
        'Commission',
        'Deposit Amount',
        'COD',
        'Collected Cod',
        'Collected COD',
        'Deposited Amount',
        'Pending Deposited Amount',
        'Rider Commission',
        'Commission Amount',
        'Delivery Charge',
        'Invoice Amount',
        'Total Tax Amount',
        'After Tax Amount',
        'Weight',
      ]
      const amountColumns = []
      this.tableFields.forEach((field, index) => {
        if (amountFieldNames.includes(field)) {
          amountColumns.push(index)
        }
      })
      return amountColumns
    },
  },
}

</script>
<style scoped>
.name {
  font-weight: bolder;
  margin-left: 5px;
  margin-top: 3px;
  color: #666666;
}
</style>
